import { FormApi } from "final-form";
import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { deleteUniversalLicense, updateUniversalLicense } from "@dashboard/devices/store";
import { License } from "@dashboard/devices/types";
import { updateProvisioning } from "@dashboard/provisioning/store/index";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning } from "@dashboard/provisioning/types";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { DialogContentText, Paper, TableContainer, TableRow } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { TextField } from "@shared/form/TextField";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useLicenses } from "./api/helpers";
import { ActionCell, ActivationCodeCell, TargetGroupCell } from "./components/Cells";

const useStyles = makeStyles()((theme) => ({
    errorAlertText: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    inputBox: {
        marginBottom: theme.spacing(4),
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(2),
    },
    input: {
        width: 300,
    },
    targetGroup: {
        fontsize: 16,
        fontWeight: 600,
    },
    tableBody: {
        "& > tr:last-child td": {
            paddingBottom: theme.spacing(3),
        },
    },
}));

type FormValues = { activationCode: string };
const columnHelper = createColumnHelper<License>();

const UniversalLicensesPage = () => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["common", "modals", "pages"]);

    const licenses = useLicenses();
    const universalLicenses = React.useMemo(() => licenses.filter((license) => !!license.universalGroupName), [licenses]);
    const provisioningSettings = useSelector(selectProvisionings);
    const isSubscriptionExpired = useCurrentSubscriptionExpired();
    const [deletingLicenseId, setDeletingLicenseId] = useState<number | null>(null);
    const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null);

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const license = licenses.find((l) => l.activationCode === values.activationCode);
        if (license?.id) {
            await dispatch(updateUniversalLicense({ id: license.id, groupId: license.groupId }))
                .unwrap()
                .then(() => showSuccessToast(t("pages:universalLicensePage.toast.targetGroupChangedSuccessfully")))
                .catch(({ message = t("pages:universalLicensePage.toast.targetGroupChangeFailed") }) => showErrorToast(message))
                .finally(() => form.restart());
        }
    };

    const validate = (values: FormValues) => {
        const licenseExists = licenses.some((l) => l.activationCode === values.activationCode);
        const activationCodeExists = universalLicenses.some((l) => l.activationCode === values.activationCode);
        const idNotMatchActivationCode = !licenses.find((l) => l.activationCode === values.activationCode)?.id;

        if (!values.activationCode) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.empty") };
        }
        if (!licenseExists) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.exist") };
        }
        if (activationCodeExists) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.alreadyAdded") };
        }
        if (idNotMatchActivationCode) {
            return { activationCode: t("pages:universalLicensePage.input.activationCode.validation.idNotFound") };
        }
    };

    const handleDelete = (license: License) => {
        setDeletingLicenseId(license.id);

        const updatedProvisionings: Provisioning[] = [];

        provisioningSettings.forEach((item) => {
            if (item.settings?.universalLicenses.includes(license.activationCode)) {
                const updatedUniversalLicenses = item.settings.universalLicenses.filter(
                    (code) => code !== license.activationCode,
                );

                const updatedItem = {
                    ...item,
                    settings: {
                        ...item.settings,
                        universalLicenses: updatedUniversalLicenses,
                    },
                };

                updatedProvisionings.push(updatedItem);
            }
        });

        Promise.all(updatedProvisionings.map((provisioning) => dispatch(updateProvisioning(provisioning)).unwrap()))
            .then(() => dispatch(deleteUniversalLicense(license.id)).unwrap())
            .then(() => showSuccessToast(t("pages:universalLicensePage.toast.licenseNotUniversal")))
            .catch(({ message = t("pages:universalLicensePage.toast.removeLicenseFailed") }) => showErrorToast(message))
            .finally(() => setDeletingLicenseId(null));
    };

    const handleDeleteClick = (license: License) => {
        const isInProvisioning = provisioningSettings.some((provisioning) =>
            provisioning.settings?.universalLicenses.includes(license.activationCode),
        );

        if (isInProvisioning) {
            setLicenseToDelete(license);
        } else {
            handleDelete(license);
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor("activationCode", {
                id: "activationCode",
                header: t("common:label.activationCode"),
                cell: (info) => <ActivationCodeCell license={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.display({
                id: "targetGroup",
                header: () => (
                    <Tooltip placement="top" title={t("pages:universalLicensePage.tooltip.targetGroup")}>
                        <span className={classes.targetGroup}>{t("pages:universalLicensePage.table.header.targetGroup")}</span>
                    </Tooltip>
                ),
                cell: (info) => <TargetGroupCell license={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (info) => (
                    <ActionCell
                        license={info.row.original}
                        isLoading={deletingLicenseId === info.row.original.id}
                        onDelete={handleDeleteClick}
                    />
                ),
                enableSorting: false,
            }),
        ],
        [t, classes.targetGroup, deletingLicenseId, handleDeleteClick],
    );

    const table = useReactTable({
        data: universalLicenses,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <PageContainer>
            <ExpiredWrapper>
                <CustomAlert severity="info">{t("pages:universalLicensePage.alert.activationCodes")}</CustomAlert>
                <CustomAlert className={classes.errorAlertText} severity="error">
                    {t("pages:universalLicensePage.alert.secret")}
                </CustomAlert>
                <Form<FormValues>
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={{ activationCode: undefined }}
                    render={({ handleSubmit, form, submitting }) => (
                        <form onSubmit={handleSubmit}>
                            <div className={classes.inputBox}>
                                <TextField name="activationCode" label={t("common:label.activationCode")} fullWidth />
                                <PrimaryButton
                                    onClick={form.submit}
                                    loading={submitting}
                                    disabled={isSubscriptionExpired}
                                    tooltipProps={{
                                        title: t("pages:universalLicensePage.tooltip.subscriptionExpired"),
                                        hide: !isSubscriptionExpired,
                                    }}
                                >
                                    {t("common:button.add")}
                                </PrimaryButton>
                            </div>
                        </form>
                    )}
                />
                <TableContainer component={Paper}>
                    <MinimalTable>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Header key={header.id} {...header} />
                                    ))}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>
                        <TableBody
                            table={table}
                            className={classes.tableBody}
                            noDataMessage={t("pages:universalLicensePage.table.noLicenses")}
                        >
                            {table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) =>
                                        flexRender(cell.column.columnDef.cell, {
                                            ...cell.getContext(),
                                            key: cell.id,
                                        }),
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </TableContainer>
            </ExpiredWrapper>
            {licenseToDelete && (
                <ConfirmDialog
                    title={t("modals:deleteUniversalLicense.title")}
                    dangerButton
                    primaryActionText={t("common:button.delete")}
                    open={!!licenseToDelete}
                    onClose={() => setLicenseToDelete(null)}
                    onConfirm={() => {
                        handleDelete(licenseToDelete);
                        setLicenseToDelete(null);
                    }}
                >
                    <DialogContentText>
                        <Trans
                            ns="common"
                            i18nKey="deleteUniversalLicense.licenseToDelete"
                            values={{ activationCode: licenseToDelete.activationCode }}
                            components={{ bold: <span style={{ fontWeight: "bold" }} /> }}
                        />
                    </DialogContentText>
                </ConfirmDialog>
            )}
        </PageContainer>
    );
};

export default withMetaDescription(UniversalLicensesPage, {
    title: i18next.t("pages:universalLicensePage.metaTitle"),
    description: i18next.t("pages:universalLicensePage.metaDescription"),
});
